import * as React from 'react';
import styled from 'styled-components';
import { Popup } from '../popup';
import { PopupMessageFooter, PopupMessageContent } from './parts';
import { IPopupMessageProps } from './popup-message-props';
import { selectThemeSemantics } from '../../styling';
import { Spinner } from '../spinner';
import { FlexBox } from '../flex-box';

export interface IPopupMessageBaseProps extends IPopupMessageProps {
    onDismiss(id?: string): void;
}

const StyledPopup = styled(Popup)`
    max-width: 500px;
    min-width: 400px;
    .ap-popup-footer { 
        padding-top: ${selectThemeSemantics(({ gap }) => gap.xxxl)};
    }
`;

export const PopupMessageItem = React.memo((props: IPopupMessageBaseProps) => {
    const {
        title, onRenderContent, type, positiveActionButtonLabel, negativeActionButtonLabel, illustrationName,
        hideNegativeActionButton, onAction, onDismiss, id, footerAlignment = 'center', message, hideCloseButton } = props;
    const [isFooterLoading, setIsFooterLoading] = React.useState<boolean>(false);

    const renderContent = React.useCallback(() => (
        <PopupMessageContent
            illustrationName={illustrationName}
            title={title}
            type={type!}
            message={message}
            onRenderContent={onRenderContent!} />
    ), [illustrationName, title, type, message, onRenderContent]);

    const _onActionClick = React.useCallback((actionStatus: boolean) => {
        if (onAction !== undefined) {
            const actionResult = onAction(actionStatus);

            if (actionStatus && actionResult instanceof Promise) {
                setIsFooterLoading(true);
                actionResult.then(() => onDismiss?.(id))
                    .catch(() => onDismiss?.(id));
                return;
            }
        }
        onDismiss?.(id);
    }, [onAction, onDismiss, id]);

    const renderFooter = React.useCallback(() => (
        isFooterLoading ? (
            <FlexBox width='100%' alignItems="center" justifyContent="center">
                <Spinner size="small" spinnerType="indeterminate" />
            </FlexBox>) :
            <PopupMessageFooter
                negativeActionButtonLabel={negativeActionButtonLabel}
                hideNegativeActionButton={hideNegativeActionButton}
                positiveActionButtonLabel={positiveActionButtonLabel}
                onActionClick={_onActionClick} type={type} />
    ), [isFooterLoading, negativeActionButtonLabel, hideNegativeActionButton, positiveActionButtonLabel, _onActionClick, type]);


    return (
        <StyledPopup
            {...props}
            title=''
            footerAlignment={footerAlignment}
            onRenderContent={renderContent}
            onRenderFooter={renderFooter}
            hideCloseButton={hideCloseButton || isFooterLoading}
        />
    );
});

PopupMessageItem.displayName = 'PopupMessageItem';
